import emojiRegex from "emoji-regex";

const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;

export const extractTemplateVariables = text => {
  const found = [];
  const source = String(text || "");
  let match;
  const pattern = new RegExp(VARIABLE_PATTERN.source, "g");
  while ((match = pattern.exec(source)) !== null) {
    const number = Number(match[1]);
    if (!found.includes(number)) found.push(number);
  }
  return found.sort((a, b) => a - b);
};

const hasEmoji = value => emojiRegex().test(String(value || ""));

const hasSequentialVariables = variables =>
  variables.every((number, index) => number === index + 1);

const exampleValues = (variables, examples) =>
  variables.map(number => String(examples?.[number] || "").trim());

const validUrl = value => {
  try {
    const parsed = new URL(String(value || ""));
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (_) {
    return false;
  }
};

const validPhone = value => /^\+?[1-9]\d{7,14}$/.test(String(value || "").replace(/[\s().-]/g, ""));

export const validateTemplateErrorV2 = ({
  name,
  category,
  language,
  headerText,
  bodyText,
  footerText,
  buttons,
  headerExamples,
  bodyExamples
}) => {
  const errors = [];
  const header = String(headerText || "");
  const body = String(bodyText || "");
  const footer = String(footerText || "");
  const normalizedButtons = Array.isArray(buttons) ? buttons : [];
  const headerVariables = extractTemplateVariables(header);
  const bodyVariables = extractTemplateVariables(body);

  if (!name || !body.trim()) errors.push("Nome e corpo da mensagem são obrigatórios.");
  if (!/^[a-z0-9_]+$/.test(String(name || ""))) {
    errors.push("O nome deve conter somente letras minúsculas, números e sublinhado (_).");
  }
  if (!category) errors.push("Escolha a categoria do template.");
  if (!language) errors.push("Escolha o idioma do template.");
  if (header.length > 60) errors.push("O cabeçalho pode ter no máximo 60 caracteres.");
  if (body.length > 1024) errors.push("O corpo pode ter no máximo 1024 caracteres.");
  if (footer.length > 60) errors.push("O rodapé pode ter no máximo 60 caracteres.");
  if (hasEmoji(header)) errors.push("Não use emoji no cabeçalho de texto.");
  if (hasEmoji(footer)) errors.push("Não use emoji no rodapé.");
  if (!hasSequentialVariables(headerVariables)) {
    errors.push("As variáveis do cabeçalho devem seguir a sequência {{1}}, {{2}}, sem pular números.");
  }
  if (!hasSequentialVariables(bodyVariables)) {
    errors.push("As variáveis do corpo devem seguir a sequência {{1}}, {{2}}, sem pular números.");
  }
  if (/^\s*\{\{\d+\}\}/.test(body) || /\{\{\d+\}\}\s*$/.test(body)) {
    errors.push("A variável não pode ser o primeiro nem o último conteúdo do corpo.");
  }
  if (exampleValues(headerVariables, headerExamples).some(value => !value)) {
    errors.push("Preencha um exemplo para cada variável do cabeçalho.");
  }
  if (exampleValues(bodyVariables, bodyExamples).some(value => !value)) {
    errors.push("Preencha um exemplo para cada variável do corpo.");
  }
  if (normalizedButtons.length > 3) errors.push("Use no máximo 3 botões.");

  normalizedButtons.forEach((button, index) => {
    const label = `Botão ${index + 1}`;
    const text = String(button?.text || "").trim();
    if (!text) errors.push(`${label}: informe o texto.`);
    if (text.length > 25) errors.push(`${label}: o texto pode ter no máximo 25 caracteres.`);
    if (hasEmoji(text)) errors.push(`${label}: não use emoji no texto.`);
    if (button?.type === "URL" && !validUrl(button.url)) {
      errors.push(`${label}: informe uma URL completa começando com http:// ou https://.`);
    }
    if (button?.type === "PHONE_NUMBER" && !validPhone(button.phone)) {
      errors.push(`${label}: informe um telefone válido com DDI.`);
    }
  });

  return { errors, headerVariables, bodyVariables };
};

export const addTemplateExamples = ({
  components,
  headerVariables,
  bodyVariables,
  headerExamples,
  bodyExamples
}) => components.map(component => {
  if (component.type === "HEADER" && headerVariables.length) {
    return {
      ...component,
      example: { header_text: exampleValues(headerVariables, headerExamples) }
    };
  }
  if (component.type === "BODY" && bodyVariables.length) {
    return {
      ...component,
      example: { body_text: [exampleValues(bodyVariables, bodyExamples)] }
    };
  }
  return component;
});
